"use client";

// ShareOptionsBar — the ONE bottom-left bar, everywhere: `share ↗` + `options ∨`.
//
// `share` opens the SHARE STUDIO — the full-bleed surface where the shared page
// is shown as the other person will meet it (the ground, the title, whose it is)
// beside the controls that set it. Sharing is the moment a thing leaves your
// hands, so it gets the screen, not a one-row popover you could miss. ONE path,
// on every surface: there is no second share menu and no OS share sheet in front
// of it (the OS sheet cannot mint a link, so it can never be the way in). Where
// there is no project to mint against — a public `/skills/<name>` page, a
// published post — the studio opens READ-ONLY on the page's own url: that page
// is already public, and the link IS its interface. `options` opens a popover ANCHORED TO ITS BUTTON (desktop
// frosted context-menu / mobile bottom sheet) carrying the actions AND the
// exports. It serves two contexts: a DOCUMENT (the open page — download/copy the
// markdown, rename) and a DIRECTORY (the level itself — the project context
// downloads that used to live in OsDock, new folder/page, rename, park,
// settings). Both variants carry the shell's undo/redo pair as the options
// menu's footer. Rides the shell bottom-left in the `dock` variant.

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, useHoverFeel } from "../../../lib/motion";
import { type OsNode } from "../../../lib/os/tree";
import { type MenuItem } from "../ContextMenu";
import OptionsMenu from "./OptionsMenu";
import Sheet from "../Sheet";
import { PillButton } from "../../ui";
import ForgetSheet, { type ForgetTarget } from "../ForgetSheet";
import ShareStudio from "../../share/ShareStudio";
import { GROUND_DIAL_EVENT } from "../GroundDial";
import { orbFace } from "../ArcPicker";
import { type Viewer } from "../../../lib/features/registry";

/** the canonical url of the page in view — what a public surface hands over.
 *  The hash is a scroll position, not part of the door; the query is (a `?v=`
 *  names which state of a skill you are reading, law_versions_rail). */
function Arrow() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path d="M3.5 8.5 8.5 3.5M4.2 3.5h4.3v4.3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 13 13" fill="none" aria-hidden>
      <path d="M3.25 5.1 6.5 8.35 9.75 5.1" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
const IcoDoc = <svg width="16" height="16" viewBox="0 0 17 17" fill="none"><path d="M4 1.5h6l3.5 3.5v10.5H4z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M10 1.5V5h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>;
const IcoZip = <svg width="16" height="16" viewBox="0 0 17 17" fill="none"><path d="M3 2.2h11v12.6H3z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M8.5 2.2v3M8.5 6.5v1.4M8.5 9v2.2" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
const IcoCopy = <svg width="16" height="16" viewBox="0 0 17 17" fill="none"><path d="M5.5 5.5h8v9h-8z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M3.5 11V2.5h8" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>;
const IcoShare = <svg width="16" height="16" viewBox="0 0 17 17" fill="none"><path d="M8.5 11V2.5M8.5 2.5 5.5 5.5M8.5 2.5l3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M4 8.5v6h9v-6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoRename = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10.5 2.5 13 5 5.5 12.5 3 13l.5-2.5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>;
const IcoDup = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M5 5h8v8H5z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M3 11V3h8" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>;
const IcoMove = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.5h4l1.2 1.5H14v6.5H2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg>;
const IcoForget = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 4.5h10M6.5 4.5V3h3v1.5M4.5 4.5l.6 8.5h5.8l.6-8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoArchive = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.5 3.5h11V6h-11z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M3.3 6v7h9.4V6M6.3 8.6h3.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoNewFolder = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4.5h4l1.2 1.5H14v6.5H2z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M8 8v3M6.5 9.5h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
const IcoNewPage = <svg width="16" height="16" viewBox="0 0 17 17" fill="none"><path d="M4 1.5h6l3.5 3.5v10.5H4z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/><path d="M8.75 8v4M6.75 10h4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg>;
const IcoDial = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2.5 5h11M2.5 11h11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/><circle cx="6" cy="5" r="1.7" stroke="currentColor" strokeWidth="1.2"/><circle cx="10" cy="11" r="1.7" stroke="currentColor" strokeWidth="1.2"/></svg>;
const IcoUndo = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3.5 6.5h6a3.5 3.5 0 0 1 0 7H6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M6 3.5 3 6.5l3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;
const IcoRedo = <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12.5 6.5h-6a3.5 3.5 0 0 0 0 7H10" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M10 3.5l3 3-3 3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>;

// the undo/redo footer row — co-located styles (the DirIcon DIRICON_SCALE_CSS
// pattern), since it renders inside the portal'd menu AND the mobile sheet.
// The ground swatch rides along: the current ground shown as the picker's own
// orb face, set in a white plaque so the colour reads as a sample being offered
// rather than a stray dot in a list.
const BAR_FOOT_CSS = `
.art-menu-foot{display:flex;gap:0.45rem;padding:0.5rem 0.55rem 0.25rem;margin-top:0.35rem;border-top:1px solid var(--hair-soft)}
.art-menu-foot button{display:flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:6px;border:1px solid var(--hair-soft);background:none;color:var(--ink-3);cursor:pointer;transition:color var(--dur-fast) ease, background var(--dur-fast) ease}
.art-menu-foot button:hover:not(:disabled){color:var(--ink);background:color-mix(in oklab, var(--ink) 6%, transparent)}
.art-menu-foot button:disabled{opacity:0.35;cursor:default}
.ctx-ico:has(.ctx-ground){width:24px;height:16px}
.ctx-ground{display:block;width:24px;height:16px;border-radius:4px;padding:2.5px;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,0.18)}
.ctx-ground i{display:block;width:100%;height:100%;border-radius:2px}
`;

/** the live ground, read straight off :root so this needs no theme provider. */
function readGround(): string {
  if (typeof window === "undefined") return "#fffaf1";
  const v = getComputedStyle(document.documentElement).getPropertyValue("--bg").trim();
  return v || "#fffaf1";
}

// the project context downloads (ported from OsDock) — one row per format.
const DIR_FORMATS: { key: string; label: string; ext: string }[] = [
  { key: "context-md", label: "download markdown", ext: "md" },
  { key: "claude", label: "download claude context", ext: "md" },
  { key: "gpt", label: "download generic context", ext: "md" },
  { key: "json", label: "download json", ext: "json" },
];

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "document";
}
function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// let the Noticer acknowledge the action in its own voice (bottom-right corner).
function notify(msg: string) {
  window.dispatchEvent(new CustomEvent("innernet:clip", { detail: { action: "note", name: msg } }));
}

/** what the bar acts on — the open document, or the directory level itself. */
export type BarContext =
  | { mode: "document"; page: OsNode }
  | { mode: "directory"; node: OsNode };

/** the shell's edit stack, surfaced as the options menu's undo/redo footer. */
export interface UndoRedoControls {
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

export default function ShareOptionsBar({
  context,
  page,
  projectId,
  projectName,
  ownerName,
  publicUrl = null,
  sharePath,
  onRename,
  onForgotten,
  onNewFolder,
  onNewFile,
  onPark,
  parked = false,
  onSettings,
  undoRedo,
  variant = "dock",
}: {
  context?: BarContext;
  /** legacy document shorthand — `context` wins when both are given. */
  page?: OsNode;
  projectId?: string | null;
  /** the folder this page lives in — the studio's face reads it as the quiet
   *  second line ("a page from …"). Optional; the face simply omits it. */
  projectName?: string | null;
  /** who is looking. The studio is open to everyone now (lib/features/registry
   *  › share-studio), so nothing here gates on it; the prop stays because the
   *  shell still hands it down and other bars may want to know. */
  viewer?: Viewer;
  /** whose page this is — the preview says it in the sharer's own name rather
   *  than "someone", because that byline is part of what is being shaped. */
  ownerName?: string | null;
  /** A PUBLIC SURFACE'S OWN DOOR. A page that is already public has no token to
   *  mint: its url IS the door, and the studio opens read-only on it. It has to
   *  be passed DELIBERATELY by such a surface (a skill page, a letter) — it used
   *  to be guessed from `location.href` whenever no project was in hand, which
   *  is why the dashboard's own root offered to share the dashboard, on a door
   *  that could never open. */
  publicUrl?: string | null;
  sharePath: string;
  /** rename the document (document mode) or the current folder (directory mode). */
  onRename?: (name: string) => void;
  /** the page was forgotten — the shell leaves it and reloads its folder. */
  onForgotten?: () => void;
  /** directory mode — create in the current level. */
  onNewFolder?: () => void;
  /** directory mode — a new page, only offered inside a project. */
  onNewFile?: () => void;
  /** directory mode, project roots — park (or bring back) this project. */
  onPark?: () => void;
  parked?: boolean;
  /** directory mode, project roots — open the project settings panel. */
  onSettings?: () => void;
  undoRedo?: UndoRedoControls;
  variant?: "dock" | "inline";
}) {
  const hover = useHoverFeel("nudge");
  const [optAnchor, setOptAnchor] = useState<{ x: number; y: number; bottom: number } | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [forgetting, setForgetting] = useState(false);
  const [draft, setDraft] = useState("");
  const [narrow, setNarrow] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [ground, setGround] = useState("#fffaf1");
  /** the page's own url, sampled when the studio opens on a surface with no
   *  project to mint against. Read at press time — never during render. */
  const optRef = useRef<HTMLButtonElement>(null);
  const renameRef = useRef<HTMLInputElement>(null);
  // Stable closers. The shell re-renders this bar often, and a fresh inline arrow
  // each time re-ran the popover's and the studio's listener effects (and handed
  // each sheet a new `onClose`) for nothing.
  const closeOptions = useCallback(() => setOptAnchor(null), []);
  const closeStudio = useCallback(() => setSheetOpen(false), []);
  const closeForget = useCallback(() => setForgetting(false), []);
  const closeRename = useCallback(() => setRenaming(false), []);

  // THE BAR OUTLIVES WHAT IT ACTS ON. The shell mounts one ShareOptionsBar in
  // the viewer and one on a level at the SAME spot in its tree, so React keeps
  // this instance (and its state) across leaving a page for a folder. A studio
  // left open on a page therefore re-opened on the next folder, and its arrival
  // effect minted a door for the folder nobody asked to share. Every popover
  // and the studio fold the moment the subject changes — a menu is about ONE
  // thing, and that thing is gone. (Reset during render, the React way, so it
  // lands before anything paints for the new subject.)
  const subjectKey = `${context?.mode ?? (page ? "document" : "none")}:${sharePath}`;
  const [seenSubject, setSeenSubject] = useState(subjectKey);
  if (seenSubject !== subjectKey) {
    setSeenSubject(subjectKey);
    setSheetOpen(false);
    setOptAnchor(null);
  }

  // mobile: there's no room for two triggers — collapse to a single "options"
  // sheet that carries BOTH the share views and the actions.
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  // the rename sheet lands on its field, name selected. The sheet moves focus to
  // its close button as it opens, so this waits a beat and takes it back; and
  // once more after the options sheet it came from has gone, because that one
  // hands focus back to its opener as it leaves.
  useEffect(() => {
    if (!renaming) return;
    const take = (select: boolean) => () => {
      const el = renameRef.current;
      if (!el || document.activeElement === el) return;
      el.focus({ preventScroll: true });
      if (select) el.select();
    };
    const a = window.setTimeout(take(true), 80);
    const b = window.setTimeout(take(false), 380);
    return () => {
      window.clearTimeout(a);
      window.clearTimeout(b);
    };
  }, [renaming]);

  // popovers anchor to their trigger's left edge, carrying BOTH the top and
  // bottom of the button's rect — ContextMenu uses both to decide whether the
  // menu fits below the trigger or needs to open upward ending at the
  // trigger's top edge (the bar sits near the bottom of the viewport, so it's
  // almost always the latter).
  const anchorOf = (el: HTMLElement | null) => {
    const r = el?.getBoundingClientRect();
    return r ? { x: r.left, y: r.top, bottom: r.bottom } : { x: 0, y: 0, bottom: 0 };
  };
  // `share` IS the studio — one press, one surface, whatever is in view. There
  // is no second share menu to fall into: a page with no project behind it is
  // already public, and the studio opens read-only on its own url.
  // ── IS THERE ANYTHING HERE TO SHARE? ──────────────────────────────────────
  // A door is either mintable against a project, or the surface already IS a
  // public page and says so. Everything else — the tasks list, the projects
  // root, `you`, a settings panel — has nothing a link could open, and offering
  // to share it opened the studio on a door that would never mint. The
  // affordance appears only where it can actually do something.
  const canShare = !!projectId || !!publicUrl;
  const openStudio = () => {
    setOptAnchor(null);
    setSheetOpen(true);
  };
  const toggleOptions = () => {
    setGround(readGround()); // sample the ground fresh, so the swatch is never stale
    setOptAnchor((a) => (a ? null : anchorOf(optRef.current)));
  };

  // "ground colour" — the one row that owns the look of the whole space. It
  // doesn't open a second picker: it blooms the dial already seated in the
  // corner, so there is one instrument and you edit the ground in place.
  const groundItem: MenuItem = {
    label: "change ground colour",
    icon: <span className="ctx-ground" aria-hidden><i style={{ background: orbFace(ground) }} /></span>,
    separated: true,
    onSelect: () => window.dispatchEvent(new CustomEvent(GROUND_DIAL_EVENT, { detail: { open: true } })),
  };

  const ctx: BarContext | null = context ?? (page ? { mode: "document", page } : null);
  const isDoc = ctx?.mode === "document";
  const subject = ctx ? (ctx.mode === "document" ? ctx.page : ctx.node) : null;
  const name = subject?.name ?? "";
  const md = (isDoc ? subject?.document || subject?.summary : subject?.summary) || "";
  const base = slug(name);

  // ---- document context ------------------------------------------------------
  // exports live under `options` now — `share` is about the door, not the file.
  const docExportItems: MenuItem[] = [
    { label: "download markdown", icon: IcoDoc, hint: ".md", separated: true, onSelect: () => { saveBlob(new Blob([md], { type: "text/markdown" }), `${base}.md`); notify(`downloaded ${base}.md`); } },
    {
      label: "download bundle",
      icon: IcoZip,
      hint: ".zip",
      onSelect: async () => {
        // loaded on demand — the zip library is ~50KB gz the dashboard never
        // needs until someone asks for a bundle.
        const { default: JSZip } = await import("jszip");
        const zip = new JSZip();
        zip.file(`${base}.md`, md);
        zip.file("meta.json", JSON.stringify({ title: name, created: subject?.createdAt ?? null, updated: subject?.lastActiveAt ?? null }, null, 2));
        saveBlob(await zip.generateAsync({ type: "blob" }), `${base}.zip`);
        notify(`downloaded ${base}.zip`);
      },
    },
    { label: "copy markdown", icon: IcoCopy, onSelect: () => { void navigator.clipboard?.writeText(md); notify(`copied ${name} as markdown`); } },
  ];

  // ── FORGET, where you are looking ─────────────────────────────────────────
  // Only a document the memory actually holds as ONE row can be forgotten on
  // its own: a page (a dimension, `dim:`) or a cell (`node:<type>-<node_id>`).
  // An artifact, a capture or a whole folder has its own lifecycle, and a
  // folder's forget already lives in its settings.
  const forgetTarget: ForgetTarget | null = (() => {
    if (!isDoc || !projectId || !subject) return null;
    if (subject.key.startsWith("dim:") && subject.editKey) {
      return { kind: "page", projectId, name: subject.editKey, title: subject.name };
    }
    if (subject.key.startsWith("node:")) {
      return { kind: "cell", projectId, name: subject.key.slice(5), title: subject.name };
    }
    return null;
  })();

  const docOptionItems: MenuItem[] = [
    { label: "rename", icon: IcoRename, onSelect: () => { setDraft(name); setRenaming(true); }, disabled: !onRename },
    { label: "duplicate", icon: IcoDup, hint: "soon", disabled: true },
    { label: "move to…", icon: IcoMove, hint: "soon", disabled: true, separated: true },
    { label: "archive", icon: IcoArchive, hint: "soon", disabled: true },
  ];
  // set apart and LAST among the things about this page — after the exports, so
  // the one irreversible act never sits between two harmless ones. It is never
  // one press from gone: it opens the sheet that shows exactly what would be
  // forgotten, and the pill in that sheet is the second ask.
  const forgetItems: MenuItem[] = forgetTarget
    ? [{ label: "forget this…", icon: IcoForget, danger: true, separated: true, onSelect: () => setForgetting(true) }]
    : [];

  // ---- directory context -----------------------------------------------------
  // share = the project context downloads (the old OsDock rows) + copy link +
  // native share. Rows that need a projectId hide at non-project levels.
  const downloadFmt = async (format: string, ext: string) => {
    if (!projectId) return;
    try {
      const res = await fetch(`/api/projects/${projectId}/inject?format=${format}`);
      const text = await res.text();
      saveBlob(new Blob([text], { type: ext === "json" ? "application/json" : "text/markdown" }), `${base}.${ext}`);
      notify(`downloaded ${base}.${ext}`);
    } catch {
      notify("couldn't download — try again");
    }
  };
  const dirExportItems: MenuItem[] = [
    ...(projectId
      ? [
          ...DIR_FORMATS.map((f, i) => ({ label: f.label, icon: IcoDoc, hint: `.${f.ext}`, separated: i === 0, onSelect: () => void downloadFmt(f.key, f.ext) }) as MenuItem),
          {
            label: "download all",
            icon: IcoZip,
            hint: ".zip",
            onSelect: () => {
              const a = document.createElement("a");
              a.href = `/api/projects/${projectId}/download`;
              a.click();
              notify(`downloading ${name} as .zip`);
            },
          } as MenuItem,
        ]
      : []),
  ];

  const dirOptionItems: MenuItem[] = [
    ...(onNewFolder ? [{ label: "new folder", icon: IcoNewFolder, onSelect: onNewFolder } as MenuItem] : []),
    ...(onNewFile ? [{ label: "new page", icon: IcoNewPage, onSelect: onNewFile } as MenuItem] : []),
    ...(onRename ? [{ label: "rename", icon: IcoRename, separated: true, onSelect: () => { setDraft(name); setRenaming(true); } } as MenuItem] : []),
    ...(onPark ? [{ label: parked ? "unpark" : "park", icon: IcoArchive, separated: !onRename, onSelect: onPark } as MenuItem] : []),
    ...(onSettings ? [{ label: "settings", icon: IcoDial, separated: !onRename && !onPark, onSelect: onSettings } as MenuItem] : []),
  ];

  // the ground row closes every options menu — it's about the space you're in,
  // not about the subject, so it reads last, under its own rule.
  const optionItems = [
    ...(isDoc ? docOptionItems : dirOptionItems),
    ...(isDoc ? docExportItems : dirExportItems),
    ...(isDoc ? forgetItems : []),
    groundItem,
  ];

  // mobile: one sheet, and `share` reads first in it — opening the SAME studio
  // the desktop `share` opens, so the moment is identical on a phone.
  const combinedItems: MenuItem[] = [
    // the phone folds share into the one sheet — and drops it on the same terms
    // the desktop trigger does.
    ...(canShare ? [{ label: "share…", icon: IcoShare, onSelect: openStudio } as MenuItem] : []),
    ...(optionItems.length ? [{ ...optionItems[0], separated: true }, ...optionItems.slice(1)] : []),
  ];

  // the undo/redo pair — rides the bottom of the OPTIONS menu on BOTH variants,
  // so you can always go back. Clicks don't close the menu (chainable), and the
  // actions narrate themselves through the Noticer.
  const footer = undoRedo ? (
    <div className="art-menu-foot" data-noswipe>
      <button type="button" aria-label="undo" title="undo" disabled={!undoRedo.canUndo} onClick={undoRedo.onUndo}>{IcoUndo}</button>
      <button type="button" aria-label="redo" title="redo" disabled={!undoRedo.canRedo} onClick={undoRedo.onRedo}>{IcoRedo}</button>
    </div>
  ) : null;

  const commitRename = () => {
    const v = draft.trim();
    if (v && v !== name) { onRename?.(v); notify(`renamed to ${v}`); }
    setRenaming(false);
  };

  if (!subject) return null;

  const noun = isDoc ? "document" : "space";
  return (
    <>
      <style>{BAR_FOOT_CSS}</style>
      {variant === "dock" && (
        <div className="os-dock-veil" aria-hidden>
          <i /><i /><i />
        </div>
      )}
      <div className={`art-bar${variant === "dock" ? " art-bar--dock" : ""}`} data-noswipe>
        {!narrow && canShare && (
          <motion.button className="os-link" data-hint={`share this ${noun} — the studio, with the page as they will meet it`} onClick={openStudio} {...hover}>
            <span className="os-link-text">share</span>
            <Arrow />
          </motion.button>
        )}
        <motion.button ref={optRef} className="os-link" data-hint={narrow ? `share, download, and actions for this ${noun}` : `options for this ${noun}`} onClick={toggleOptions} {...hover}>
          <span className="os-link-text">options</span>
          <Chevron />
        </motion.button>
      </div>

      <OptionsMenu open={!!optAnchor} onClose={closeOptions} items={narrow ? combinedItems : optionItems} anchor={optAnchor} title={narrow ? name : "options"} footer={footer} />

      {/* keyed on the path: a studio is opened ON one thing, and never
          re-targets in place if the shell moves underneath it. With a project
          behind it the studio mints; without one the page is already public and
          the studio opens read-only on the page's own url. */}
      {sheetOpen && (
        <ShareStudio
          key={sharePath}
          target={{
            projectId: projectId ?? null,
            path: sharePath,
            name,
            projectName: projectName ?? null,
            publicUrl: projectId ? null : publicUrl,
            // what the face renders: the page or folder in hand, so the preview
            // is instant and exists before a link does.
            node: subject,
            ownerName: ownerName ?? null,
          }}
          onClose={closeStudio}
        />
      )}

      {forgetting && forgetTarget && (
        <ForgetSheet
          target={forgetTarget}
          onClose={closeForget}
          onForgotten={(title) => {
            setForgetting(false);
            notify(`forgot ${title}`);
            onForgotten?.();
          }}
        />
      )}

      {/* rename: the sheet, small. The name is the field; save is the one action. */}
      <Sheet
        open={renaming}
        onClose={closeRename}
        labelledBy="art-rename-title"
        title="rename"
        footStart={
          <button type="button" className="quiet-link" onClick={() => setRenaming(false)}>
            cancel
          </button>
        }
        footer={
          <PillButton type="button" onClick={commitRename} disabled={!draft.trim()}>
            save
          </PillButton>
        }
      >
        <input
          ref={renameRef}
          className="input-soft"
          value={draft}
          aria-labelledby="art-rename-title"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Esc is the sheet's: it closes before this input hears it
            if (e.key === "Enter" && !e.nativeEvent.isComposing) commitRename();
          }}
        />
      </Sheet>
    </>
  );
}
